import { defineStore } from 'pinia';
import { computed, ref } from 'vue';

const TOKEN_KEY = 'faforge-token';
const PROFILE_KEY = 'faforge-profile';

interface UserProfile {
  email: string;
  name: string;
  picture?: string;
  isAdmin?: boolean;
}

interface JwtPayload {
  exp: number;
}

function decodeJwt(token: string): JwtPayload | null {
  try {
    const payload = token.split('.')[1];
    if (!payload) return null;
    const json = atob(payload.replace(/-/g, '+').replace(/_/g, '/'));
    return JSON.parse(json) as JwtPayload;
  } catch {
    return null;
  }
}

/**
 * Holds the FA-Forge-issued JWT (NOT Google's id_token). The API mints it after a
 * successful server-side Google OAuth code exchange. Same pattern as FA Nexus.
 */
export const useAuthStore = defineStore('auth', () => {
  const token = ref<string | null>(localStorage.getItem(TOKEN_KEY));
  const profile = ref<UserProfile | null>(
    (() => {
      const raw = localStorage.getItem(PROFILE_KEY);
      return raw ? (JSON.parse(raw) as UserProfile) : null;
    })()
  );

  const isAuthenticated = computed(() => {
    if (!token.value) return false;
    const payload = decodeJwt(token.value);
    if (!payload?.exp) return false;
    return payload.exp * 1000 > Date.now();
  });

  const user = computed(() => profile.value);
  // Admin gating for the SPA (Admin nav group + /admin routes). Authoritative enforcement is
  // server-side via [Authorize(Roles="Admin")]; this just drives what the UI shows.
  const isAdmin = computed(() => profile.value?.isAdmin === true);

  function setAuth(t: string, p: UserProfile) {
    token.value = t;
    profile.value = p;
    localStorage.setItem(TOKEN_KEY, t);
    localStorage.setItem(PROFILE_KEY, JSON.stringify(p));
  }

  function signOut() {
    token.value = null;
    profile.value = null;
    localStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem(PROFILE_KEY);
  }

  return { token, profile, user, isAdmin, isAuthenticated, setAuth, signOut };
});
